import React from 'react';
import styled from 'styled-components';

import {
  CheckoutLabelGrouping,
  CheckoutLabel,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import { mediaQueries } from 'shared/styles';
import { GqlAllotmentInfo, AllotmentStatus } from 'types/graphql';

const FL_MMUR_REGISTRY_URL = 'https://knowthefactsmmj.com/registry/';

export function formatRemaining(remaining: number, unit: string): string {
  const rounded = Math.round(remaining * 100) / 100;
  if (unit === 'oz') {
    return `${rounded} oz`;
  }
  if (rounded >= 1000) {
    return `${rounded.toLocaleString()} mg`;
  }
  return `${rounded} mg`;
}

type MMURAllotmentProps = {
  allotmentInfo: GqlAllotmentInfo | null;
  loading: boolean;
};

export function MMURAllotment({ allotmentInfo, loading }: MMURAllotmentProps): JSX.Element | null {
  if (loading) {
    return (
      <CheckoutLabelGrouping>
        <CheckoutLabel>
          <CheckoutSectionHeading>MMUR Limits</CheckoutSectionHeading>
        </CheckoutLabel>
        <CheckoutBody>
          <AllotmentCard>
            <HelperText>Checking allotment limits...</HelperText>
          </AllotmentCard>
        </CheckoutBody>
      </CheckoutLabelGrouping>
    );
  }

  if (!allotmentInfo) {
    return null;
  }

  const { details } = allotmentInfo;

  return (
    <CheckoutLabelGrouping>
      <CheckoutLabel>
        <CheckoutSectionHeading>MMUR Limits</CheckoutSectionHeading>
      </CheckoutLabel>
      <CheckoutBody>
        <AllotmentCard>
          <HelperText>
            View more details about your MMUR limits by logging into{' '}
            <RegistryLink href={FL_MMUR_REGISTRY_URL} target='_blank' rel='noopener noreferrer'>
              Florida Health Medical Marijuana Use Registry
            </RegistryLink>
          </HelperText>
          <Divider />
          <CategoryList>
            {details?.map((detail) =>
              detail ? (
                <CategoryRow key={detail.category}>
                  <CategoryName>{detail.category}</CategoryName>
                  <CategoryRemaining exceeded={detail.status === AllotmentStatus.exceeded}>
                    {formatRemaining((detail.limit ?? 0) - (detail.used ?? 0), detail.unit ?? 'mg')} Remaining
                  </CategoryRemaining>
                </CategoryRow>
              ) : null
            )}
          </CategoryList>
        </AllotmentCard>
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
}

const AllotmentCard = styled.div`
  background: #fff;
  border: 1px solid #d8dee3;
  border-radius: 5px;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.07);
  padding: 20px;
`;

const HelperText = styled.p`
  color: #646d72;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.065px;
  margin: 0;
`;

const RegistryLink = styled.a`
  color: #0555ae;
  text-decoration: underline;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e3e7e9;
  margin: 20px 0;
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CategoryRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media ${mediaQueries.largePhone} {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const CategoryName = styled.span`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #2c3236;
  width: 140px;
  flex-shrink: 0;
`;

const CategoryRemaining = styled.span<{ exceeded: boolean }>`
  font-size: 14px;
  line-height: 20px;
  color: ${({ exceeded }) => (exceeded ? '#be0517' : '#2c3236')};
  white-space: nowrap;
`;
